import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  differenceInCalendarDays,
  format,
  isValid,
  parseISO,
  startOfToday,
} from "date-fns";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { Skeleton } from "primereact/skeleton";
import CopyableIdentifier from "../../components/controls/CopyableIdentifier";

import { useDashboardViewModel } from "../../viewmodels/dashboard/DashboardViewModel";
import { wipLabel } from "../../shared/tenantLabels";

import "./dashboard.css";

function formatInt(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 });
}

function formatQty(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const num = Number(n);
  return num.toLocaleString("en-US", {
    maximumFractionDigits: Math.abs(num) >= 100 ? 0 : 2,
  });
}

function parseDay(value) {
  if (!value) return null;
  try {
    const d = typeof value === "string" ? parseISO(value.slice(0, 10)) : value;
    return isValid(d) ? d : null;
  } catch {
    return null;
  }
}

function formatShortDate(value) {
  const d = parseDay(value);
  return d ? format(d, "MMM d") : "—";
}

function formatRelativeUpdated(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return format(d, "h:mm a");
  } catch {
    return null;
  }
}

/** overdue | today | future | none */
function expectedDateUrgency(value) {
  const d = parseDay(value);
  if (!d) return "none";
  const diff = differenceInCalendarDays(d, startOfToday());
  if (diff < 0) return "overdue";
  if (diff === 0) return "today";
  return "future";
}

function daysOpen(createdAt) {
  const d = parseDay(createdAt);
  if (!d) return null;
  return Math.max(0, differenceInCalendarDays(startOfToday(), d));
}

function CountBadge({ count, tone = "default" }) {
  const toneClass =
    tone === "alert"
      ? "dashboard-count-badge alert"
      : tone === "warning"
        ? "dashboard-count-badge warning"
        : tone === "info"
          ? "dashboard-count-badge info"
          : "dashboard-count-badge";

  return (
    <span className={toneClass} aria-hidden={count == null}>
      {count == null ? "—" : formatInt(count)}
    </span>
  );
}

function StatusBadge({ label, tone = "slate" }) {
  return <span className={`dashboard-status-badge ${tone}`}>{label}</span>;
}

function TableSkeletonRows({ cols = 5, rows = 5 }) {
  return Array.from({ length: rows }).map((_, i) => (
    <tr key={i}>
      {Array.from({ length: cols }).map((__, j) => (
        <td key={j} className="px-3 py-2.5">
          <Skeleton width={j === 1 ? "70%" : "50%"} height="0.75rem" />
        </td>
      ))}
    </tr>
  ));
}

function EmptyRow({ colSpan, message }) {
  return (
    <tr>
      <td
        colSpan={colSpan}
        className="px-3 py-10 text-center text-sm text-slate-500"
      >
        {message}
      </td>
    </tr>
  );
}

function Panel({ title, count, tone, viewAllTo, children }) {
  return (
    <section className="dashboard-panel flex min-h-0 flex-col">
      <header className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <h2 className="truncate text-sm font-semibold text-slate-900">
            {title}
          </h2>
          <CountBadge count={count} tone={tone} />
        </div>
        <Link
          to={viewAllTo}
          className="shrink-0 text-xs font-semibold text-slate-500 hover:text-slate-900"
        >
          View all
        </Link>
      </header>
      <div className="dashboard-panel-body min-h-0 flex-1 overflow-auto">
        {children}
      </div>
    </section>
  );
}

function shipmentStatusTone(statusCode) {
  if (Number(statusCode) === 3) return "blue";
  return "slate";
}

function receivingStatusTone(status) {
  return status === "Open" ? "amber" : "slate";
}

function expectedDateClass(urgency) {
  if (urgency === "overdue") return "tabular-nums font-semibold text-rose-600";
  if (urgency === "today") return "tabular-nums font-semibold text-amber-600";
  if (urgency === "future") return "tabular-nums font-medium text-blue-600";
  return "tabular-nums text-slate-500";
}

function rowNavProps(path) {
  if (!path) return {};
  return {
    className: "dashboard-row-link",
    onClick: () => window.open(path, "_blank", "noopener,noreferrer"),
    onKeyDown: (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        window.open(path, "_blank", "noopener,noreferrer");
      }
    },
    tabIndex: 0,
    role: "link",
  };
}

function Dashboard({ logout }) {
  const tenant = useSelector((state) => state.auth.tenant);
  const wip = wipLabel(tenant);
  const wipPlural = `${wip}s`;

  const { activity, loading, fetchError, refresh } =
    useDashboardViewModel(logout);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const counts = activity?.counts ?? {};
  const openWipRows = activity?.open_wips ?? [];
  const pendingRows = activity?.pending_shipments ?? [];
  const recentRows = activity?.recent_invoices ?? [];
  const updatedAt = formatRelativeUpdated(activity?.meta?.generated_at);

  const reorderRows = useMemo(() => {
    const rows = activity?.reorder_alerts ?? [];
    return [...rows].sort(
      (a, b) => (Number(a.short) || 0) - (Number(b.short) || 0)
    );
  }, [activity?.reorder_alerts]);

  return (
    <main
      className="min-h-screen bg-slate-100 p-4 text-slate-950 antialiased sm:p-6 lg:p-8"
      aria-busy={loading}
    >
      <div className="mx-auto flex max-w-[1600px] flex-col gap-5">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">
              Operations dashboard
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {updatedAt
                ? `Live status — updated ${updatedAt}`
                : "Inventory, production, receiving, and shipments."}
            </p>
          </div>
          <button
            type="button"
            onClick={() => refresh()}
            disabled={loading}
            className="inline-flex items-center gap-2 self-start rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-60"
          >
            <ArrowPathIcon
              className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
              aria-hidden
            />
            Refresh
          </button>
        </header>

        {fetchError ? (
          <div
            className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800"
            role="alert"
          >
            {fetchError}
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2 xl:items-stretch">
          <Panel
            title="Recently received"
            count={counts["recent-receiving"]}
            tone="info"
            viewAllTo="/receiving"
          >
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Supplier</th>
                  <th>Status</th>
                  <th className="text-right">Weight</th>
                  <th className="text-right">Pieces</th>
                  <th>Received</th>
                </tr>
              </thead>
              <tbody>
                {loading && !activity ? (
                  <TableSkeletonRows cols={6} />
                ) : recentRows.length === 0 ? (
                  <EmptyRow colSpan={6} message="No recent receiving." />
                ) : (
                  recentRows.map((row) => {
                    const id = row.id;
                    return (
                      <tr
                        key={id || row.invoice_number}
                        {...rowNavProps(id ? `/receiving/${id}` : null)}
                      >
                        <td className="font-medium text-slate-800">
                          {row.invoice_number || "—"}
                        </td>
                        <td className="max-w-[12rem] truncate text-slate-700">
                          {row.supplier_name || "—"}
                        </td>
                        <td>
                          <StatusBadge
                            label={row.status || "—"}
                            tone={receivingStatusTone(row.status)}
                          />
                        </td>
                        <td className="text-right tabular-nums text-slate-700">
                          {formatQty(row.total_kg)} kg
                        </td>
                        <td className="text-right tabular-nums text-slate-700">
                          {formatQty(row.total_pieces)}
                        </td>
                        <td className="text-slate-600">
                          {formatShortDate(row.received_at)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </Panel>

          <Panel
            title="In production"
            count={counts["open-wips"]}
            tone="warning"
            viewAllTo="/wips"
          >
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>Lot</th>
                  <th>Room</th>
                  <th className="text-right">Cartons</th>
                  <th className="text-right">Net kg</th>
                  <th>Started</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading && !activity ? (
                  <TableSkeletonRows cols={6} />
                ) : openWipRows.length === 0 ? (
                  <EmptyRow
                    colSpan={6}
                    message={`No open ${wipPlural.toLowerCase()}.`}
                  />
                ) : (
                  openWipRows.map((row) => {
                    const id = row.wip_id ?? row.id;
                    const openDays = daysOpen(row.created_at);
                    const aged = openDays != null && openDays >= 3;
                    return (
                      <tr
                        key={id || `${row.lot_number}-${row.room_name}`}
                        {...rowNavProps(id ? `/wips/${id}` : null)}
                      >
                        <td className="font-medium text-slate-800">
                          <CopyableIdentifier
                            value={row.lot_number}
                            description="lot number"
                          />
                        </td>
                        <td className="max-w-[10rem] truncate text-slate-700">
                          {row.room_name || "—"}
                        </td>
                        <td className="text-right tabular-nums text-slate-700">
                          {formatInt(row.carton_count)}
                        </td>
                        <td className="text-right tabular-nums text-slate-700">
                          {formatQty(row.total_kg)}
                        </td>
                        <td
                          className={
                            aged
                              ? "font-semibold tabular-nums text-amber-700"
                              : "text-slate-600"
                          }
                        >
                          {formatShortDate(row.created_at)}
                          {aged ? (
                            <span className="ml-1.5 text-xs font-bold">
                              {openDays}d
                            </span>
                          ) : null}
                        </td>
                        <td>
                          <StatusBadge label="Open" tone="amber" />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </Panel>

          <Panel
            title="Shipments to go out"
            count={counts["pending-shipments"]}
            tone="info"
            viewAllTo="/shipments"
          >
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Customer</th>
                  <th>Status</th>
                  <th className="text-right">Cartons</th>
                  <th>Expected</th>
                </tr>
              </thead>
              <tbody>
                {loading && !activity ? (
                  <TableSkeletonRows cols={5} />
                ) : pendingRows.length === 0 ? (
                  <EmptyRow
                    colSpan={5}
                    message="No shipments waiting to go out."
                  />
                ) : (
                  pendingRows.map((row) => {
                    const id = row.order_id;
                    const urgency = expectedDateUrgency(row.expected_date);
                    return (
                      <tr
                        key={id || row.order_number}
                        {...rowNavProps(id ? `/shipments/${id}` : null)}
                      >
                        <td className="font-medium text-slate-800">
                          {row.order_number || "—"}
                        </td>
                        <td className="max-w-[12rem] truncate text-slate-700">
                          {row.customer_name || "—"}
                        </td>
                        <td>
                          <StatusBadge
                            label={row.status || "—"}
                            tone={shipmentStatusTone(row.status_code)}
                          />
                        </td>
                        <td className="text-right tabular-nums text-slate-700">
                          {formatInt(row.total_cartons)}
                        </td>
                        <td className={expectedDateClass(urgency)}>
                          {formatShortDate(row.expected_date)}
                          {urgency === "overdue" ? (
                            <span className="ml-1 text-xs font-bold">
                              overdue
                            </span>
                          ) : urgency === "today" ? (
                            <span className="ml-1 text-xs font-bold">
                              today
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </Panel>

          <Panel
            title="Low inventory"
            count={counts.reorder}
            tone="alert"
            viewAllTo="/low-in-stock"
          >
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>PLU</th>
                  <th>Product</th>
                  <th className="text-right">On hand</th>
                  <th className="text-right">Reorder at</th>
                  <th className="text-right">Short</th>
                </tr>
              </thead>
              <tbody>
                {loading && !activity ? (
                  <TableSkeletonRows cols={5} />
                ) : reorderRows.length === 0 ? (
                  <EmptyRow
                    colSpan={5}
                    message="No products below reorder."
                  />
                ) : (
                  reorderRows.map((row) => {
                    const id = row.id;
                    const short = Number(row.short);
                    return (
                      <tr
                        key={id || `${row.plu}-${row.product_name}`}
                        {...rowNavProps(id ? `/products/${id}` : null)}
                      >
                        <td className="font-medium text-slate-800">
                          {row.plu || "—"}
                        </td>
                        <td className="max-w-[12rem] truncate text-slate-700">
                          {row.product_name || "—"}
                        </td>
                        <td className="text-right tabular-nums text-slate-700">
                          {formatQty(row.on_hand)} {row.on_hand_unit || ""}
                        </td>
                        <td className="text-right tabular-nums text-slate-500">
                          {formatQty(row.reorder_at)} {row.on_hand_unit || ""}
                        </td>
                        <td
                          className={`text-right tabular-nums font-semibold ${
                            short < 0 ? "text-rose-600" : "text-slate-700"
                          }`}
                        >
                          {formatQty(row.short)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </Panel>
        </div>
      </div>
    </main>
  );
}

export default Dashboard;
